/**
 * Sticky Add to Cart (mobile PDP)
 *
 * Shows a sticky bar at the bottom of the viewport once the static/main Add to
 * Cart button has been scrolled completely past. Clicking the sticky button
 * simply proxies a click to the real Add to Cart button so all existing
 * add-to-cart logic (variant validation, ajax cart, analytics, etc.) keeps
 * working untouched.
 *
 * Whenever the shopper picks a different variant, `component-product-form.js`
 * dispatches a `product:variant-change` event on `document`. This component
 * listens for that event and mirrors the image, price and sold-out/disabled
 * state of the main Add to Cart button so the sticky bar always reflects the
 * currently selected variant.
 */
class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.targetSelector = this.dataset.targetButton;
    this.staticButton = this.targetSelector ? document.querySelector(this.targetSelector) : null;
    this.stickyButton = this.querySelector('[data-sticky-atc-button]');
    this.stickyButtonText = this.querySelector('[data-sticky-atc-button-text]');
    this.priceEl = this.querySelector('[data-sticky-atc-price]');
    this.imageWrapperEl = this.querySelector('[data-sticky-atc-image-wrapper]');
    this.imageEl = this.querySelector('[data-sticky-atc-image]');

    if (!this.staticButton || !this.stickyButton) return;

    this.isVisible = false;

    this.stickyButton.addEventListener('click', this.onStickyButtonClick.bind(this));

    this.onVariantChangeHandler = this.onVariantChange.bind(this);
    document.addEventListener('product:variant-change', this.onVariantChangeHandler);

    this.createVisibilityObserver();
    // Fallback safety net: keeps the sticky button's disabled state in sync
    // with the static button even if something updates it without firing
    // the `product:variant-change` event.
    this.createButtonStateObserver();
    this.syncButtonStateFromStatic();
  }

  disconnectedCallback() {
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.buttonObserver) this.buttonObserver.disconnect();
    if (this.onVariantChangeHandler) document.removeEventListener('product:variant-change', this.onVariantChangeHandler);
  }

  onStickyButtonClick() {
    if (this.staticButton.disabled || this.staticButton.classList.contains('btn-disabled')) return;
    this.staticButton.click();
  }

  /**
   * Reveal the sticky bar once the static Add to Cart button has scrolled
   * fully above the viewport, hide it again once it's back in view.
   */
  createVisibilityObserver() {
    this.visibilityObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleVisibility(scrolledPast);
    }, {
      threshold: 0,
    });

    this.visibilityObserver.observe(this.staticButton);
  }

  toggleVisibility(show) {
    if (show === this.isVisible) return;
    this.isVisible = show;
    this.classList.toggle('is-visible', show);
  }

  /**
   * Mirror the currently selected variant (image, price, sold-out/disabled
   * state) into the sticky bar. `variant` is `false` when the chosen option
   * combination has no matching variant at all -- the same case that
   * disables the main Add to Cart button -- so the sticky button is
   * disabled too in that case.
   */
  onVariantChange(event) {
    const variant = event.detail && event.detail.variant;

    if (!variant) {
      this.applyButtonState(true, window.variantStrings && window.variantStrings.unavailable);
      return;
    }

    this.syncPrice(variant);
    this.syncImage(variant);
    this.applyButtonState(!variant.available);
  }

  syncPrice(variant) {
    if (!this.priceEl || typeof variant.price !== 'number') return;
    this.priceEl.innerHTML = Shopify.formatMoney(variant.price, window.globalVariables.money_format);
  }

  syncImage(variant) {
    if (!this.imageEl) return;

    if (!variant.featured_image) {
      if (this.imageWrapperEl) this.imageWrapperEl.classList.add('d-none');
      return;
    }

    const src = Utility.getSizedImageUrl(variant.featured_image, '100x100');
    if (!src) return;

    this.imageEl.src = src;
    this.imageEl.removeAttribute('srcset');
    if (variant.featured_image.alt) this.imageEl.alt = variant.featured_image.alt;
    if (this.imageWrapperEl) this.imageWrapperEl.classList.remove('d-none');
  }

  /**
   * Match the main Add to Cart button's sold-out/unavailable behavior:
   * disable the button, apply `.btn-disabled` only (no `bg-gray-400`),
   * drop `btn-primary` while unavailable, and swap the label.
   */
  applyButtonState(isDisabled, label) {
    if (!this.stickyButton) return;

    if (isDisabled) {
      this.stickyButton.setAttribute('disabled', true);
      this.stickyButton.classList.add('btn-disabled');
    } else {
      this.stickyButton.removeAttribute('disabled');
      this.stickyButton.classList.remove('btn-disabled', 'bg-gray-400');
    }

    if (!this.stickyButtonText || !window.variantStrings) return;
    this.stickyButtonText.textContent = label || (isDisabled ? window.variantStrings.soldOut : window.variantStrings.addToCart);
  }

  /**
   * Fallback: keep the sticky button's enabled/disabled state + label in sync
   * with the static Add to Cart button in case it changes outside of the
   * `product:variant-change` event (e.g. other scripts toggling it directly).
   */
  createButtonStateObserver() {
    this.buttonObserver = new MutationObserver(this.syncButtonStateFromStatic.bind(this));
    this.buttonObserver.observe(this.staticButton, { attributes: true, attributeFilter: ['disabled', 'class'] });

    // Also watch the label node so Sold Out / Unavailable / Add to Cart text
    // stays mirrored when the main CTA updates its `.add-text`.
    const addText = this.staticButton.querySelector('.add-text');
    if (addText) {
      this.buttonObserver.observe(addText, { characterData: true, childList: true, subtree: true });
    }
  }

  syncButtonStateFromStatic() {
    const isDisabled = this.staticButton.disabled || this.staticButton.classList.contains('btn-disabled');
    const addText = this.staticButton.querySelector('.add-text');
    const label = addText ? addText.textContent.trim() : undefined;
    this.applyButtonState(isDisabled, label);
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
